import type { ProvidedProps } from '@visx/shape/lib/shapes/Pie';

import type { PiePartitionConfig } from '@blockworks/platform/api/research/studio/visualizations';
import { cn } from '@blockworks/ui/system';

import { UseVisualizationTooltipReturn } from '../../../utils/use-visualization-tooltip';
import { getContrastColor } from '../utils';

import { PieTooltipData } from './pie.model';
import { getArcOptions, type PieOptions } from './utils';

type PieArcsProps = {
    pie: ProvidedProps<PiePartitionConfig>;
    partitions: PiePartitionConfig[];
    aggregrate: number;
    showPartitionLabels: boolean;
    isDonut: boolean;
    radius: PieOptions['radius'];
} & Pick<UseVisualizationTooltipReturn<PieTooltipData>, 'showTooltip' | 'hideTooltip' | 'tooltipData'>;

const PieArcs = ({
    pie,
    partitions,
    aggregrate,
    showPartitionLabels,
    showTooltip,
    hideTooltip,
    tooltipData,
    isDonut,
    radius,
}: PieArcsProps) => {
    return pie.arcs.map(arc => {
        const { centroidX, centroidY, label, color, isSmallSlice, percentage, value, format } = getArcOptions({
            pie,
            arc,
            partitions,
            aggregrate,
            isDonut,
            radius,
        });

        return (
            <g
                key={`arc-${arc.index}`}
                onMouseMove={event =>
                    showTooltip(event, {
                        label,
                        value,
                        percentage,
                        format,
                    })
                }
                onMouseOut={hideTooltip}
            >
                <path d={pie.path(arc) || ''} fill={color} opacity={tooltipData?.value === value ? 0.9 : 0.75} />
                {showPartitionLabels && !isSmallSlice && !!percentage && (
                    <text
                        x={centroidX}
                        y={centroidY}
                        dy=".35em"
                        textAnchor="middle"
                        pointerEvents="none"
                        className={cn(`text-xs pointer-events-none`)}
                        style={{ fill: getContrastColor(color) }}
                    >
                        {percentage}%
                    </text>
                )}
            </g>
        );
    });
};

export { PieArcs };
